import { createAsyncThunk } from '@reduxjs/toolkit';
import apiCall, { getTokens } from '../AuthorizedApi';
import { getHostUrl } from '../getHostUrl';
import type {
  Invoice,
  InvoiceSummary,
  PaginatedInvoices,
  InvoiceListParams,
  InvoiceWritePayload,
  CreateInvoiceFromOrderPayload,
  SetInstallmentsPayload,
  RecordPaymentPayload,
  CreatePaymentLinkPayload,
  InvoicePayment,
  InvoiceInstallment,
  InvoiceDashboardStats,
} from '../../../Data/invoice.dto';

const buildQueryUrl = (
  base: string,
  params?: Record<string, string | number | boolean | undefined>
): string => {
  const q = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
    });
  }
  const s = q.toString();
  return `${base}${s ? `?${s}` : ''}`;
};

export const getAllInvoicesAPI = createAsyncThunk(
  'invoice/getAllInvoicesAPI',
  async (params: InvoiceListParams | undefined, { rejectWithValue }) => {
    try {
      const url = buildQueryUrl('/invoice/invoices/', params as any);
      const response: PaginatedInvoices = await apiCall({ method: 'GET', url });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createInvoiceAPI = createAsyncThunk(
  'invoice/createInvoiceAPI',
  async (data: InvoiceWritePayload, { rejectWithValue }) => {
    try {
      const response: Invoice = await apiCall({
        method: 'POST',
        url: '/invoice/invoices/',
        data,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getInvoiceNextNumberAPI = async (): Promise<{ invoice_no: string }> =>
  apiCall({ method: 'GET', url: '/invoice/invoices/next-number/' });

export const createInvoiceFromOrderAPI = async (
  data: CreateInvoiceFromOrderPayload
): Promise<Invoice> =>
  apiCall({ method: 'POST', url: '/invoice/invoices/from-order/', data });

export const getInvoiceByIdAPI = async (id: string | number): Promise<Invoice> =>
  apiCall({ method: 'GET', url: `/invoice/invoices/${id}/` });

export const updateInvoiceAPI = async (
  id: string | number,
  data: Partial<InvoiceWritePayload>
): Promise<Invoice> =>
  apiCall({ method: 'PATCH', url: `/invoice/invoices/${id}/`, data });

export const replaceInvoiceAPI = async (
  id: string | number,
  data: InvoiceWritePayload
): Promise<Invoice> =>
  apiCall({ method: 'PUT', url: `/invoice/invoices/${id}/`, data });

export const deleteInvoiceAPI = async (id: string | number): Promise<void> =>
  apiCall({ method: 'DELETE', url: `/invoice/invoices/${id}/` });

export const setInstallmentsAPI = async (
  id: string | number,
  data: SetInstallmentsPayload
): Promise<InvoiceInstallment[]> =>
  apiCall({ method: 'PUT', url: `/invoice/invoices/${id}/installments/`, data });

export const recordPaymentAPI = async (
  id: string | number,
  data: RecordPaymentPayload
): Promise<InvoicePayment> =>
  apiCall({ method: 'POST', url: `/invoice/invoices/${id}/payments/`, data });

export const uploadInvoicePdfAPI = async (
  id: string | number,
  pdfBlob: Blob,
  filename: string
): Promise<{ share_id?: string }> => {
  const { accessToken } = getTokens();
  const form = new FormData();
  form.append('pdf', pdfBlob, filename);

  const response = await fetch(
    `${getHostUrl()}/invoice/invoices/${id}/upload-pdf/`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: form,
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.message || data?.error?.message || 'Upload failed');
  }
  return data;
};

export const shareInvoiceWhatsAppAPI = async (
  id: string | number,
  pdfBlob: Blob,
  filename: string
): Promise<{ sent_to?: string }> => {
  const { accessToken } = getTokens();
  const form = new FormData();
  form.append('pdf', pdfBlob, filename);
  form.append('template_category', 'utility');

  const response = await fetch(
    `${getHostUrl()}/invoice/invoices/${id}/share-whatsapp/`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: form,
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.message || data?.error?.message || 'Share failed');
  }
  return data;
};

export const createPaymentLinkAPI = async (
  invoiceId: string | number,
  installmentId: string | number,
  data: CreatePaymentLinkPayload
): Promise<{ link_url: string; payment_link_id?: string }> =>
  apiCall({
    method: 'POST',
    url: `/invoice/invoices/${invoiceId}/installments/${installmentId}/create-payment-link/`,
    data,
  });

export const sharePaymentLinkWhatsAppAPI = async (
  invoiceId: string | number,
  installmentId: string | number
): Promise<{ sent_to?: string }> =>
  apiCall({
    method: 'POST',
    url: `/invoice/invoices/${invoiceId}/installments/${installmentId}/share-payment-whatsapp/`,
  });

export const getInvoiceDashboardStatsAPI = async (params?: {
  from_date?: string;
  to_date?: string;
  branch_id?: number | null;
}): Promise<InvoiceDashboardStats> => {
  const url = buildQueryUrl('/invoice/dashboard/stats/', params as any);
  return apiCall({ method: 'GET', url });
};

